import isEqual from 'lodash.isequal';
import { MetricResult } from '../../../../api/types';
import { MetricId } from '../../../../api/types-graphql';
import { UndefinedValue } from '../../../../constants';
import { useMetricDetailsMapContext } from '../../../../context/contexts';
import { MetricIdTypeArrays, SegmentBy } from '../../../../types';
import { extractSegmentValue } from '../../../hooks';
import { LanguageString } from '../../dashboards/types';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  useChartTitleForGroupByChart,
  useMetricDisplayValues,
  useMetricQueries,
} from '../common/hooks';
import {
  DataOrError,
  ExternalChartComponentProps,
  MetricQueryConfig,
  ValidationResponse,
} from '../common/types';
import {
  TimePeriodSegmentedVizConfig,
  TimePeriodSegmentedVizData,
  TimePeriodSegmentedVizDataResponse,
} from './types';

const validateResponseHasCorrectSegmentsForSingleQuery = (
  metricResult: MetricResult,
  config: TimePeriodSegmentedVizConfig
): ValidationResponse => {
  const { groupBy, segmentBy } = config.query;
  // Checking if first segment is groupBy and second is segmentBy if it exists
  try {
    const groupSegments = metricResult.segments[0].groupSegments;
    // Validating groupSegments are correct
    groupSegments.forEach((groupSegment) => {
      if (
        groupSegment.groupSegment.length &&
        !isEqual(
          {
            dataType: groupSegment.groupSegment[0].dataType,
            dataField: groupSegment.groupSegment[0].dataField,
          },
          groupBy.dataField
        )
      ) {
        throw new Error('Error: groupBy field does not match');
      }
      if (segmentBy && groupSegment.groupSegment.length > 1) {
        // This can be extended to any number of segment
        const { dataField: segmentByDataField, dataType: segmentByDataType } =
          groupSegment.groupSegment[1];
        if (
          !isEqual(
            { dataField: segmentByDataField, dataType: segmentByDataType },
            segmentBy.dataField
          )
        ) {
          throw new Error('Error: segmentBy field does not match');
        }
      }
    });
  } catch (e) {
    return {
      isValid: false,
      error: new Error(
        `Error: metricResult does not have correct segments: ${e}`
      ),
    };
  }
  return { isValid: true, error: null };
};

type ChartTitleSingleQueryProps = {
  config: TimePeriodSegmentedVizConfig;
  metricSql: string | null;
  externalProps: ExternalChartComponentProps;
  titleFromConfig?: LanguageString;
};

const useChartTitleSingleQuery = (
  props: ChartTitleSingleQueryProps
): TitleConfig => {
  const { config, metricSql, externalProps } = props;
  const { metric } = config.query;
  const segmentations: SegmentBy[] = [config.query.groupBy];
  if (config.query.segmentBy) {
    segmentations.push(config.query.segmentBy);
  }
  return useChartTitleForGroupByChart({
    externalProps: props.externalProps,
    metrics: [metric] as MetricIdTypeArrays,
    filters: config.query.filters,
    timeSelection: config.query.timeSelection,
    metricsSql: [metricSql],
    segmentations,
    titleFromConfig: props.titleFromConfig,
  });
};

const useChartTimePeriodSingleQueryConfigToDataResponse = (
  config: TimePeriodSegmentedVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<TimePeriodSegmentedVizDataResponse> => {
  const { timeSliderState, segmentationState, filtersState } = externalProps;
  const { query } = config;
  const segmentations: SegmentBy[] = [query.groupBy];
  if (query.segmentBy) {
    segmentations.push(query.segmentBy);
  }
  const queries: MetricQueryConfig[] = [
    {
      metrics: [query.metric] as MetricIdTypeArrays,
      timeSelection: query.timeSelection,
      filters: query.filters,
      segmentations,
    },
  ];
  const { queryResponses } = useMetricQueries({
    queries,
    timeSliderState,
    segmentationState,
    filtersState,
  });
  // As there is only one query
  const response = queryResponses[0];
  const { data, error } = response;
  if (error) {
    return { data: null, error };
  }
  const firstMetricResult = data[0];
  const { isValid, error: validationError } =
    validateResponseHasCorrectSegmentsForSingleQuery(firstMetricResult, config);
  if (!isValid) {
    return { data: null, error: validationError };
  }
  const groupSegmentsWithValues =
    firstMetricResult.segments[0].groupSegments.filter(
      (groupSegment) => Number(groupSegment.data) !== 0
    );
  const segmentByDataField = query.segmentBy.dataField;
  const groupObj: Record<string, Record<string, number>> = {};
  // for segmentation response, I don't know why backend includes some segments where
  // values are 0, but I don't need them so filtering out below

  groupSegmentsWithValues.forEach((groupSegment) => {
    const groupBySegment = groupSegment.groupSegment[0];
    const segmentBySegment = groupSegment.groupSegment[1];
    const groupVal = groupBySegment.value as string | null;
    const segmentVal = extractSegmentValue(
      segmentBySegment.value,
      query.segmentBy
    ) as string | null;
    if (!groupObj[groupVal ?? UndefinedValue]) {
      groupObj[groupVal ?? UndefinedValue] = {};
    }
    groupObj[groupVal ?? UndefinedValue][segmentVal ?? UndefinedValue] =
      (groupSegment.data ?? 0) as number;
  });
  const groups = Object.entries(groupObj).map(([groupVal, segmentValObj]) => {
    const segmentVals = Object.entries(segmentValObj).map(
      ([segmentVal, value]) => {
        return {
          segmentVal: segmentVal === UndefinedValue ? null : segmentVal,
          value,
        };
      }
    );
    return {
      groupVal: groupVal === UndefinedValue ? null : groupVal,
      segmentByDataField,
      segmentVals,
    };
  });
  const finalResponse: TimePeriodSegmentedVizDataResponse = {
    metric: query.metric,
    groupBy: query.groupBy,
    groups,
    metricSql: firstMetricResult.meta.metricSql,
  };
  return { data: finalResponse, error: null };
};

export const useTimePeriodSegmentedConfigToData = (
  config: TimePeriodSegmentedVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<TimePeriodSegmentedVizData> => {
  const { data, error } = useChartTimePeriodSingleQueryConfigToDataResponse(
    config,
    externalProps
  );
  const metricDetailsMap = useMetricDetailsMapContext();
  const metricDisplayValues = useMetricDisplayValues([
    config.query.metric.value as unknown as MetricId,
  ]);
  const titleConfig = useChartTitleSingleQuery({
    config,
    metricSql: data?.metricSql ?? '',
    externalProps,
    titleFromConfig: config.title,
  });
  if (error) {
    return { data: null, error };
  }
  return {
    data: {
      dataResponse: data,
      titleConfig,
      metricDetails: metricDetailsMap,
      metricDisplayValues,
      chartColors: config.chartColors,
    },
    error: null,
  };
};
